import React, { useState, useEffect, useMemo } from "react";
import {
  useTable,
  useSortBy,
  usePagination,
  useFilters,
  useGlobalFilter,
} from "react-table";
import { useBuildingAggregates } from "./hooks/useBuildingAggregates";
import AlertTrendsCard from "./AlertTrendsCard";
import SortIndicator from "../../components/ui/SortIndicator";
import TrendBadge from "../../components/ui/TrendBadge";
import PriorityBadge from "../../components/ui/PriorityBadge";
import "./tableStyle.scss";

function alertCountColor(v) {
  if (v <= 0) return "text-ink-3";
  return "";
}

function alertCountStyle(v) {
  if (v <= 0) return undefined;
  if (v < 2) return { color: "#F1C40F" }; // yellow
  if (v < 5) return { color: "#FB8C00" }; // orange
  if (v < 10) return { color: "#E74C3C" }; // red
  return { color: "#B00020" }; // deep red
}

function AggregateCell({ value, status, tone }) {
  if (status === "loading") {
    return <span className="text-ink-3" aria-label="Loading">…</span>;
  }
  if (status === "error" || value == null) {
    return (
      <span
        className="text-ink-3"
        title="Could not load this value"
        aria-label="Unavailable"
      >
        -
      </span>
    );
  }
  if (tone === "fire") {
    return (
      <span
        className={`font-semibold ${alertCountColor(value)}`}
        style={alertCountStyle(value)}
      >
        {value}
      </span>
    );
  }
  // Other tones: simple binary color (zero muted, non-zero accented).
  const colorClass =
    value > 0
      ? tone === "info"
        ? "text-alert-info font-semibold"
        : ""
      : tone
        ? "text-ink-3"
        : "";
  return <span className={colorClass}>{value}</span>;
}

// Sort addresses by street name (from the first letter) first, then by the
// leading house number, so "50 Brown Street" comes before "100 Brown Street"
// while streets still group alphabetically.
function parseAddress(name) {
  const s = String(name || "").trim();
  const firstLetter = s.search(/[a-zA-Z]/);
  if (firstLetter === -1) return { street: s, number: parseFloat(s) || 0 };
  const num = s.slice(0, firstLetter).match(/\d+/);
  return {
    street: s.slice(firstLetter),
    number: num ? parseInt(num[0], 10) : Infinity,
  };
}

function sortName(rowA, rowB) {
  const a = parseAddress(rowA.original.name);
  const b = parseAddress(rowB.original.name);
  const byStreet = a.street.localeCompare(b.street, undefined, {
    numeric: true,
    sensitivity: "base",
  });
  return byStreet !== 0 ? byStreet : a.number - b.number;
}

export default function BuildingEventsTable(props) {
  const [activeBuildingId, setActiveBuildingId] = useState(null);

  const handleMouseEnter = (row) => {
    setActiveBuildingId(row.buildingId);
  };
  const handleRowClick = (row) => props.loadUnitsData(row.buildingId);

  // Per-building Units + Alerts aggregates (Alerts respects the date range).
  const { aggregates, allAlertRecords } = useBuildingAggregates(
    props.data,
    props.startDate,
    props.endDate
  );

  // Merge aggregates into the row data so react-table can sort on them.
  const tableData = useMemo(
    () =>
      (props.data || []).map((b) => {
        const a = aggregates.get(b.buildingId);
        return {
          ...b,
          unitsCount: a?.unitsCount ?? null,
          onlineCount: a?.onlineCount ?? null,
          alertsCount: a?.alertsCount ?? null,
          shutdownsCount: a?.shutdownsCount ?? null,
          hasPCO: a?.hasPCO ?? false,
          trendStatus: a?.trendStatus ?? null,
          trendRank: a?.trendRank ?? null,
          trendLevel: a?.trendLevel ?? null,
          priorityScore: a?.priorityScore ?? null,
          priorityBand: a?.priorityBand ?? null,
          aggStatus: a?.aggStatus ?? "loading",
        };
      }),
    [props.data, aggregates]
  );

  // Treat unloaded values as -Infinity so they cluster together rather than
  // jumping around as queries resolve. Stable sort keeps ties in place.
  const numericSort = (key) => (rowA, rowB) => {
    const a =
      rowA.original[key] == null ? Number.NEGATIVE_INFINITY : rowA.original[key];
    const b =
      rowB.original[key] == null ? Number.NEGATIVE_INFINITY : rowB.original[key];
    if (a === b) return 0;
    return a > b ? 1 : -1;
  };

  const columns = React.useMemo(
    () => [
      {
        Header: "Address",
        accessor: "name",
        sortType: sortName,
        filter: "fuzzyText",
        isVisible: true,
      },
      {
        Header: "Priority",
        accessor: "priorityScore",
        sortType: numericSort("priorityScore"),
        description:
          "How problematic this property is right now, based on current danger and how fast it's worsening.",
        Cell: ({ row }) => {
          const { priorityScore, priorityBand, aggStatus } = row.original;
          if (priorityScore == null) {
            return aggStatus === "error" ? (
              <span className="text-ink-3" title="Could not load this value">
                -
              </span>
            ) : (
              <span className="text-ink-3" aria-label="Loading">
                …
              </span>
            );
          }
          return (
            <div className="flex items-center justify-center">
              <PriorityBadge score={priorityScore} band={priorityBand} />
            </div>
          );
        },
        isVisible: true,
      },
      {
        Header: "Sensors Online",
        accessor: "onlineCount",
        sortType: numericSort("onlineCount"),
        Cell: ({ row }) => {
          const { onlineCount, unitsCount, aggStatus } = row.original;
          if (onlineCount == null || unitsCount == null) {
            return aggStatus === "error" ? (
              <span
                className="text-ink-3"
                title="Could not load this value"
                aria-label="Unavailable"
              >
                -
              </span>
            ) : (
              <span className="text-ink-3" aria-label="Loading">
                …
              </span>
            );
          }
          const offline = unitsCount - onlineCount;
          return (
            <span title={`${onlineCount} online, ${offline} offline`}>
              {onlineCount} / {unitsCount}
            </span>
          );
        },
        isVisible: true,
      },
      {
        Header: "Trend",
        accessor: "trendRank",
        sortType: numericSort("trendRank"),
        description:
          "Aggregate safety trend across this property's sensors over the last 4 weeks vs the prior 4 weeks.",
        Cell: ({ row }) => {
          const { trendStatus } = row.original;
          if (trendStatus == null) {
            return (
              <span className="text-ink-3" aria-label="Unavailable">
                -
              </span>
            );
          }
          return (
            <div className="flex items-center justify-center">
              <TrendBadge level={row.original.trendLevel} status={trendStatus} />
            </div>
          );
        },
        isVisible: true,
      },
      {
        Header: "Alerts",
        accessor: "alertsCount",
        sortType: numericSort("alertsCount"),
        description: "Alerts in the selected date range.",
        Cell: ({ row }) => (
          <AggregateCell
            value={row.original.alertsCount}
            status={row.original.aggStatus}
            tone="fire"
          />
        ),
        isVisible: true,
      },
      {
        Header: "Safety\nShut Offs",
        accessor: "shutdownsCount",
        sortType: numericSort("shutdownsCount"),
        description: "Safety shut offs in the selected date range.",
        Cell: ({ row }) => {
          if (
            row.original.aggStatus === "ready" &&
            !row.original.hasPCO
          ) {
            return (
              <span
                className="text-ink-3 font-semibold"
                title="No sensors at this property have a power cutoff unit attached."
              >
                N/A
              </span>
            );
          }
          return (
            <AggregateCell
              value={row.original.shutdownsCount}
              status={row.original.aggStatus}
              tone="info"
            />
          );
        },
        isVisible: true,
      },
    ],
    []
  );

  const filterTypes = React.useMemo(
    () => ({
      text: (rows, id, filterValue) =>
        rows.filter((row) => {
          const rowValue = row.values[id];
          return rowValue !== undefined
            ? String(rowValue)
                .toLowerCase()
                .startsWith(String(filterValue).toLowerCase())
            : true;
        }),
    }),
    []
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    setGlobalFilter,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    state: { pageIndex, pageSize },
  } = useTable(
    {
      columns,
      data: tableData,
      filterTypes,
      initialState: {
        hiddenColumns: columns
          .filter((column) => !column.isVisible)
          .map((column) => column.accessor),
        sortBy: [{ id: "priorityScore", desc: true }],
        pageIndex: 0,
        pageSize: 10,
      },
    },
    useFilters,
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  // Apply the search query coming from the page header.
  useEffect(() => {
    setGlobalFilter(props.searchQuery || undefined);
  }, [props.searchQuery, setGlobalFilter]);

  // Pad the page out to a fixed row count so the table height never collapses
  // as the user filters. Empty rows render as transparent placeholders.
  const paddedPage = useMemo(() => {
    const padded = [...page];
    while (padded.length < pageSize) padded.push(null);
    return padded;
  }, [page, pageSize]);

  useEffect(() => {
    if (props.data && props.data.length > 0) {
      setActiveBuildingId(props.data[0].buildingId);
    } else {
      setActiveBuildingId(null);
    }
  }, [props.data]);

  return (
    <div className="mt-2">
      <h1 className="text-2xl font-semibold text-white mb-5">
        {props.organization ? `${props.organization} Properties` : "Properties"}
      </h1>

      {props.data.length === 0 ? (
        <div className="rounded-card border border-dashed border-white/25 p-10 text-center bg-white/5">
          <h2 className="text-2xl font-semibold text-white">
            No site, property, or building was found
          </h2>
          <p className="mt-3 text-ink-3">
            Install at least one CTS Smart Kitchen Sensor with the phone app
            and reload this page.
          </p>
        </div>
      ) : (
        <>
        <div className="grid grid-cols-1 xl:grid-cols-[3fr_2fr] gap-6 items-start">
          <div className="cts-table-card">
            <table className="cts-table" {...getTableProps()}>
              <thead>
                {headerGroups.map((headerGroup) => (
                  <tr {...headerGroup.getHeaderGroupProps()}>
                    {headerGroup.headers.map((column) => {
                      const sortState = column.isSorted
                        ? column.isSortedDesc
                          ? "desc"
                          : "asc"
                        : "none";
                      return (
                        <th
                          {...column.getHeaderProps(
                            column.getSortByToggleProps({
                              title: `Sort by ${column.render("Header")}${
                                column.description
                                  ? `: ${column.description}`
                                  : ""
                              }`,
                            })
                          )}
                        >
                          <span className="inline-flex items-center">
                            {column.render("Header")}
                            <span className="cts-sort">
                              <SortIndicator state={sortState} />
                            </span>
                          </span>
                        </th>
                      );
                    })}
                  </tr>
                ))}
              </thead>
              <tbody {...getTableBodyProps()}>
                {paddedPage.map((row, idx) => {
                  if (!row) {
                    return (
                      <tr key={`empty-${idx}`} className="cts-empty-row">
                        <td colSpan={columns.length}>&nbsp;</td>
                      </tr>
                    );
                  }
                  prepareRow(row);
                  const isActive =
                    row.original.buildingId === activeBuildingId;
                  return (
                    <tr
                      onMouseEnter={() => handleMouseEnter(row.original)}
                      onClick={() => handleRowClick(row.original)}
                      {...row.getRowProps({
                        className: isActive ? "is-active" : "",
                      })}
                    >
                      {row.cells.map((cell) => (
                        <td {...cell.getCellProps()}>
                          {cell.render("Cell")}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div className="cts-pagination">
              <button onClick={() => gotoPage(0)} disabled={!canPreviousPage}>
                {"«"}
              </button>
              <button
                onClick={() => previousPage()}
                disabled={!canPreviousPage}
              >
                {"‹"}
              </button>
              {Array.from({ length: 5 }, (_, i) => {
                const pageNumber = pageIndex + 1 + i;
                const isDisabled = pageNumber > pageOptions.length;
                return (
                  <button
                    key={pageNumber}
                    disabled={isDisabled}
                    onClick={() => !isDisabled && gotoPage(pageNumber - 1)}
                    style={
                      pageNumber - 1 === pageIndex
                        ? {
                            background: "rgba(187,218,98,0.2)",
                            borderColor: "#BBDA62",
                          }
                        : undefined
                    }
                  >
                    {pageNumber}
                  </button>
                );
              })}
              <button onClick={() => nextPage()} disabled={!canNextPage}>
                {"›"}
              </button>
              <button
                onClick={() => gotoPage(pageCount - 1)}
                disabled={!canNextPage}
              >
                {"»"}
              </button>
            </div>
          </div>
          <AlertTrendsCard
            records={allAlertRecords}
            title="Property History"
          />
        </div>
        </>
      )}
    </div>
  );
}
